import React from 'react';
import {
  Folder,
  CalendarDays,
  GraduationCap,
  Briefcase,
  Building2,
  Users,
  Lightbulb,
  Microscope,
  Activity,
  Plane,
  Film,
  Home,
  Utensils,
  Laptop,
  Trophy,
  ShoppingCart,
  Target,
  UsersRound,
  Scale,
  ClipboardList,
  BarChart3,
  Video,
  Youtube,
  Code,
  DollarSign,
  BarChart,
  Settings,
  PhoneCall,
  Layout,
  FileText,
  Mic2,
  Music2,
  Radio,
  Headphones,
  Speaker,
  Podcast,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useUserTags from '../../../../hooks/useTags';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';

const AVAILABLE_ICONS = [
  { name: 'Folder', icon: Folder },
  { name: 'Video', icon: Video },
  { name: 'Youtube', icon: Youtube },
  { name: 'Mic', icon: Mic2 },
  { name: 'Music', icon: Music2 },
  { name: 'Radio', icon: Radio },
  { name: 'Headphones', icon: Headphones },
  { name: 'Speaker', icon: Speaker },
  { name: 'Podcast', icon: Podcast },
  { name: 'Code', icon: Code },
  { name: 'DollarSign', icon: DollarSign },
  { name: 'BarChart', icon: BarChart },
  { name: 'Settings', icon: Settings },
  { name: 'PhoneCall', icon: PhoneCall },
  { name: 'Layout', icon: Layout },
  { name: 'FileText', icon: FileText },
  { name: 'CalendarDays', icon: CalendarDays },
  { name: 'GraduationCap', icon: GraduationCap },
  { name: 'Briefcase', icon: Briefcase },
  { name: 'Building2', icon: Building2 },
  { name: 'Users', icon: Users },
  { name: 'Lightbulb', icon: Lightbulb },
  { name: 'Microscope', icon: Microscope },
  { name: 'Activity', icon: Activity },
  { name: 'Plane', icon: Plane },
  { name: 'Film', icon: Film },
  { name: 'Home', icon: Home },
  { name: 'Utensils', icon: Utensils },
  { name: 'Laptop', icon: Laptop },
  { name: 'Trophy', icon: Trophy },
  { name: 'ShoppingCart', icon: ShoppingCart },
  { name: 'Target', icon: Target },
  { name: 'UsersRound', icon: UsersRound },
  { name: 'Scale', icon: Scale },
  { name: 'ClipboardList', icon: ClipboardList },
  { name: 'BarChart3', icon: BarChart3 },
];

interface FolderIconProps {
  open: boolean;
  onClose: () => void;
  folderId: string;
  currentIcon?: string;
}

const FolderIcon: React.FC<FolderIconProps> = ({
  open,
  onClose,
  folderId,
  currentIcon = 'Folder',
}) => {
  const { setTag, getTag } = useUserTags();
  const { t } = useTranslation();
  const folderIconsTag = getTag('folder_icons');
  const folderIcons = folderIconsTag ? JSON.parse(folderIconsTag) : {};
  const [selectedIcon, setSelectedIcon] = React.useState(currentIcon);

  const handleIconSelect = (iconName: string) => {
    setSelectedIcon(iconName);
  };

  const handleSave = () => {
    const updatedIcons = { ...folderIcons, [folderId]: selectedIcon };
    setTag({ key: 'folder_icons', value: JSON.stringify(updatedIcons) });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('library.folder.actions.changeIcon', 'Change Icon')}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-6 gap-2 py-4">
          {AVAILABLE_ICONS.map(({ name, icon: Icon }) => (
            <button
              key={name}
              type="button"
              onClick={() => handleIconSelect(name)}
              className={`
                flex items-center justify-center p-3 rounded-lg aspect-square transition-all
                ${
                  selectedIcon === name
                    ? 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground'
                    : 'bg-transparent hover:bg-accent hover:text-accent-foreground'
                }
              `}
            >
              <Icon className="h-5 w-5" />
            </button>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleSave} disabled={!selectedIcon}>
            {t('library.folder.actions.applyIcon', 'Apply Icon')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FolderIcon;
export { AVAILABLE_ICONS };
